import { useLatest } from 'ahooks';
import { cloneDeep } from 'lodash';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { ImageLayer } from '@meitu/saas-editor-core/es/layers/image-layer';
import { IImageLayerProps, ILayerProps } from '@meitu/saas-editor-core/es/version-types';

interface ActiveDataContextType {
    activeLayer: ILayerProps | null;
    setActiveData: (layer: ILayerProps | null) => void;
}

const ActiveDataContext = createContext<ActiveDataContextType | null>(null);

export const useActiveData = () => {
    const context = useContext(ActiveDataContext);
    if (!context) {
        throw new Error('useActiveData must be used within an ActiveDataProvider');
    }
    return context;
};

export const ActiveDataProvider: React.FC<{
    children: React.ReactNode;
    editor: Editor;
}> = ({ children, editor }) => {
    const originLayer = useRef<ILayerProps | null>(null);
    const [activeLayer, setActiveData] = useState<ILayerProps | null>(null);
    const [activeLayerId, setActiveDataId] = useState<string | null>(editor.active.getPick()?.id || null);
    const latestLayer = useLatest(activeLayer);

    useEffect(() => {
        const handler = editor.active.onChange((layer, pickLayer) => {
            const operateLayer = pickLayer || layer;
            if (latestLayer.current !== operateLayer && operateLayer) {
                const currentActiveLayer = cloneDeep(operateLayer);
                (currentActiveLayer.data as IImageLayerProps).effect = (
                    originLayer.current?.data as IImageLayerProps
                )?.effect;
                (currentActiveLayer.data as IImageLayerProps).borderRadius = (
                    originLayer.current?.data as IImageLayerProps
                )?.borderRadius;
                originLayer.current = currentActiveLayer;
            }
            // console.log('ot active onChange', latestLayer.current === operateLayer);
            setActiveDataId(pickLayer?.id || layer?.id || null);
        });
        return () => {
            editor.off(handler);
        };
    }, [editor]);

    useEffect(() => {
        if (activeLayerId) {
            const layer = editor.stage.getLayer(activeLayerId, true);
            originLayer.current = cloneDeep(layer);
            setActiveData(layer);
            editor.getInstance(ImageLayer).showImageEffectRenderState(activeLayerId, false);
        }
        return () => {
            if (activeLayerId) {
                const originEffect = (originLayer.current?.data as IImageLayerProps)?.effect || '';
                const originBorderRadius = (originLayer.current?.data as IImageLayerProps)?.borderRadius || '';
                const originStr = JSON.stringify(originEffect) + JSON.stringify(originBorderRadius);
                const latestEffect = (latestLayer.current?.data as IImageLayerProps)?.effect || '';
                const latestBorderRadius = (latestLayer.current?.data as IImageLayerProps)?.borderRadius || '';
                const latestStr = JSON.stringify(latestEffect) + JSON.stringify(latestBorderRadius);
                console.log('ot effect', { originStr, latestStr, layer: editor.stage.getLayer(activeLayerId) });
                if (originStr !== latestStr && originLayer.current && latestLayer.current) {
                    (async (latestLayer, originLayer) => {
                        const url = await editor.getInstance(ImageLayer).getEffectScreenshotUrl(latestLayer);
                        const effect = (latestLayer?.data as IImageLayerProps)?.effect;
                        if (url && effect) {
                            effect.screenshotUrl = url;
                        }
                        editor.getInstance(ImageLayer).changeImageEffectAttr(latestLayer, originLayer);
                    })(latestLayer.current, originLayer.current);
                } else {
                    editor.getInstance(ImageLayer).showImageEffectRenderState(activeLayerId, false);
                }
            }
        };
    }, [activeLayerId]);

    return <ActiveDataContext.Provider value={{ activeLayer, setActiveData }}>{children}</ActiveDataContext.Provider>;
};
